import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import WalletCard from '../../components/wallet/WalletCard';

interface Appointment {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  payment_status: string;
  notes: string | null;
  appointment_services: Array<{
    service_id: string;
    services: {
      name: string;
      duration: number;
      price: number;
    } | null;
  }> | null;
}

const ProfilePage: React.FC = () => {
  const { userProfile, user, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [pastAppointments, setPastAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    preferred_name: '',
    phone_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: ''
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isGeneratingPass, setIsGeneratingPass] = useState(false);

  // Show error message from redirect (e.g., from AdminRoute)
  useEffect(() => {
    const state = location.state as any;
    if (state?.error) {
      setErrorMessage(state.error);
      // Clear the error from location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Initialize form data from userProfile
  useEffect(() => {
    if (userProfile) {
      setFormData({
        full_name: userProfile.full_name || '',
        preferred_name: userProfile.preferred_name || '',
        phone_number: userProfile.phone_number || '',
        address_line1: userProfile.address_line1 || '',
        address_line2: userProfile.address_line2 || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        zip_code: userProfile.zip_code || ''
      });
    }
  }, [userProfile]);

  // Fetch appointments
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('appointments')
          .select(`
            id,
            start_time,
            end_time,
            status,
            payment_status,
            notes,
            staff_id,
            team!appointments_staff_id_fkey (
              name,
              role,
              photo
            ),
            appointment_services (
              service_id,
              services (
                name,
                duration,
                price
              )
            )
          `)
          .eq('client_id', user.id)
          .order('start_time', { ascending: true });

        if (error) {
          return;
        }

        const now = new Date();
        const appointments = (data || []) as unknown as Appointment[];
        
        // Filter out cancelled appointments
        const activeAppointments = appointments.filter(apt => apt.status !== 'cancelled');
        const upcoming = activeAppointments.filter(apt => new Date(apt.start_time) >= now);
        const past = activeAppointments.filter(apt => new Date(apt.start_time) < now);

        setUpcomingAppointments(upcoming);
        setPastAppointments(past);
      } catch (error) {
        // Silent fail
      } finally {
        setIsLoading(false);
      }
    };

    fetchAppointments();

    // Setup realtime subscription for appointment changes
    if (user?.id) {
      const channel = supabase
        .channel('customer-appointments-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'appointments',
            filter: `client_id=eq.${user.id}`
          },
          (payload) => {
            // Refresh appointments when any change occurs
            fetchAppointments();
          }
        )
        .subscribe();

      // Cleanup subscription on unmount
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update(formData)
        .eq('id', user.id);

      if (error) {
        setErrorMessage('Failed to update profile. Please try again.');
        return;
      }

      setSaveMessage('Profile updated successfully!');
      setEditMode(false);
      // Refresh user profile in AuthContext
      await refreshUserProfile();

      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setErrorMessage('An unexpected error occurred.');
    }
  };

  const formatDate = (dateString: string) => {
    // DB stores timestamp without timezone, so add 'Z' to parse as UTC
    // This matches how admin reads appointments
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const utcDate = new Date(utcDateString);
    
    // Format using Texas timezone
    return utcDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3A5E63]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-semibold text-[#3A5E63] mb-2">
            Welcome, {userProfile?.preferred_name || userProfile?.full_name || 'Guest'}!
          </h1>
          <p className="text-[#344854]/70">Manage your profile and appointments</p>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {errorMessage}
            <button onClick={() => setErrorMessage('')} className="ml-4 text-sm underline">
              Dismiss
            </button>
          </div>
        )}

        {/* Success Message */}
        {saveMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {saveMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Info & Membership */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-[#3A5E63]">Profile Information</h2>
                {!editMode && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="text-sm text-[#3A5E63] hover:underline"
                  >
                    Edit
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Full Name
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                    />
                  ) : (
                    <p className="text-[#344854]">{userProfile?.full_name || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Preferred Name
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="preferred_name"
                      value={formData.preferred_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                    />
                  ) : (
                    <p className="text-[#344854]">{userProfile?.preferred_name || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Email
                  </label>
                  <p className="text-[#344854]">{userProfile?.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Phone Number
                  </label>
                  {editMode ? (
                    <input
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                    />
                  ) : (
                    <p className="text-[#344854]">{userProfile?.phone_number || 'Not set'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#344854] mb-1">
                    Address
                  </label>
                  {editMode ? (
                    <>
                      <input
                        type="text"
                        name="address_line1"
                        value={formData.address_line1}
                        onChange={handleInputChange}
                        placeholder="Address Line 1"
                        className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63] mb-2"
                      />
                      <input
                        type="text"
                        name="address_line2"
                        value={formData.address_line2}
                        onChange={handleInputChange}
                        placeholder="Address Line 2 (optional)"
                        className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63] mb-2"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="City"
                          className="px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                        />
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="State"
                          className="px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                        />
                      </div>
                      <input
                        type="text"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        placeholder="ZIP Code"
                        className="w-full px-3 py-2 border border-[#B2D8C5] rounded focus:outline-none focus:ring-2 focus:ring-[#3A5E63] mt-2"
                      />
                    </>
                  ) : (
                    <div className="text-[#344854]">
                      {userProfile?.address_line1 ? (
                        <>
                          <p>{userProfile.address_line1}</p>
                          {userProfile.address_line2 && <p>{userProfile.address_line2}</p>}
                          <p>
                            {userProfile.city}, {userProfile.state} {userProfile.zip_code}
                          </p>
                        </>
                      ) : (
                        <p>Not set</p>
                      )}
                    </div>
                  )}
                </div>

                {editMode && (
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handleSaveProfile}
                      className="flex-1 bg-[#3A5E63] text-white py-2 rounded hover:bg-[#2d4a4d] transition-colors"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        // Reset form data
                        setFormData({
                          full_name: userProfile?.full_name || '',
                          preferred_name: userProfile?.preferred_name || '',
                          phone_number: userProfile?.phone_number || '',
                          address_line1: userProfile?.address_line1 || '',
                          address_line2: userProfile?.address_line2 || '',
                          city: userProfile?.city || '',
                          state: userProfile?.state || '',
                          zip_code: userProfile?.zip_code || ''
                        });
                      }}
                      className="px-4 py-2 border border-[#B2D8C5] text-[#344854] rounded hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Membership Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Membership Status</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#344854]">Status:</span>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      userProfile?.membership_status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {userProfile?.membership_status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {userProfile?.membership_status === 'active' && (
                  <p className="text-sm text-green-600">
                    You get 10% off all services!
                  </p>
                )}

                {userProfile?.membership_status !== 'active' && (
                  <button
                    onClick={() => navigate('/join')}
                    className="w-full bg-[#B2D8C5] text-white py-2 rounded hover:bg-[#88bfa3] transition-colors"
                  >
                    Join Membership
                  </button>
                )}
              </div>
            </div>

            {/* Wallet */}
            <WalletCard />

            {/* Apple Wallet */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Digital Wallet Pass</h2>
              <p className="text-sm text-[#344854]/70 mb-4">
                Add your membership card to Apple Wallet for quick access to your balance and member benefits.
              </p>
              <button
                onClick={async () => {
                  if (isGeneratingPass) return;

                  setIsGeneratingPass(true);
                  setErrorMessage('');

                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session?.access_token) {
                      setErrorMessage('Please log in to download your wallet pass');
                      return;
                    }

                    const response = await fetch(
                      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-wallet-pass`,
                      {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${session.access_token}`,
                          'Content-Type': 'application/json',
                        },
                      }
                    );

                    const data = await response.json();

                    if (!response.ok) {
                      throw new Error(data.error || 'Failed to get wallet pass');
                    }

                    if (data.hasPass && data.passUrl) {
                      // Navigate directly to the pass URL
                      // On iOS, this triggers the "Add to Apple Wallet" prompt
                      // On desktop, this downloads the .pkpass file
                      window.location.href = data.passUrl;
                    } else {
                      // Pass not yet generated
                      setErrorMessage(data.message || 'Your wallet pass is being prepared. Please try again in a few minutes.');
                    }
                  } catch (error: any) {
                    setErrorMessage(error.message || 'Failed to download wallet pass');
                  } finally {
                    setIsGeneratingPass(false);
                  }
                }}
                disabled={isGeneratingPass}
                className={`w-full bg-black text-white py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  isGeneratingPass ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-800'
                }`}
              >
                {isGeneratingPass ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating Pass...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    Add to Apple Wallet
                  </>
                )}
              </button>
              <p className="text-xs text-[#344854]/50 mt-2 text-center">
                Works on iPhone and Apple Watch
              </p>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/booking')}
                  className="w-full bg-[#3A5E63] text-white py-2 rounded hover:bg-[#2d4a4d] transition-colors"
                >
                  Book Appointment
                </button>
                <button
                  onClick={() => navigate('/services')}
                  className="w-full border border-[#B2D8C5] text-[#344854] py-2 rounded hover:bg-gray-50 transition-colors"
                >
                  View Services
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Appointments */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming Appointments */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Upcoming Appointments</h2>
              {upcomingAppointments.length === 0 ? (
                <div className="text-center py-8 text-[#344854]/70">
                  <p className="mb-4">No upcoming appointments</p>
                  <button
                    onClick={() => navigate('/booking')}
                    className="bg-[#3A5E63] text-white px-6 py-2 rounded hover:bg-[#2d4a4d] transition-colors"
                  >
                    Book Now
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingAppointments.map((apt) => (
                    <div
                      key={apt.id}
                      className="border border-[#B2D8C5] rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-[#3A5E63]">
                            {formatDate(apt.start_time)}
                          </p>
                          {/* @ts-ignore - team relation */}
                          {apt.team && (
                            <p className="text-sm text-[#344854] mt-1 font-medium">
                              {/* @ts-ignore */}
                              with {apt.team.name}
                            </p>
                          )}
                          <div className="text-sm text-[#344854]/70 mt-1">
                            {apt.appointment_services && apt.appointment_services.map((as, idx) => (
                              <span key={idx}>
                                {as.services?.name}
                                {idx < (apt.appointment_services?.length || 0) - 1 && ', '}
                              </span>
                            ))}
                          </div>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            apt.payment_status === 'paid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {apt.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                        </span>
                      </div>
                      {apt.notes && (
                        <p className="text-sm text-[#344854]/70 mt-2">Note: {apt.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Past Appointments */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Appointment History</h2>
              {pastAppointments.length === 0 ? (
                <p className="text-center py-8 text-[#344854]/70">No past appointments</p>
              ) : (
                <div className="space-y-3">
                  {pastAppointments.slice(0, 5).map((apt) => (
                    <div
                      key={apt.id}
                      className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-[#344854]">
                            {formatDate(apt.start_time)}
                          </p>
                          <p className="text-xs text-[#344854]/70 mt-1">
                            {apt.appointment_services && apt.appointment_services.map((as, idx) => (
                              <span key={idx}>
                                {as.services?.name}
                                {idx < (apt.appointment_services?.length || 0) - 1 && ', '}
                              </span>
                            ))}
                          </p>
                        </div>
                        <span className="text-xs text-[#344854]/70">
                          {apt.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {pastAppointments.length > 5 && (
                    <p className="text-sm text-center text-[#344854]/70 pt-2">
                      Showing 5 most recent
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
